import { useEffect, useRef, useState } from 'react'
import { Editor, EditorContent, useEditor } from '@tiptap/react'
import CharacterCount from '@tiptap/extension-character-count'
import Document from '@tiptap/extension-document'
import Text from '@tiptap/extension-text'
import Paragraph from '@tiptap/extension-paragraph'
import Placeholder from '@tiptap/extension-placeholder'
import { EditorView } from '@tiptap/pm/view'
import clsx from 'clsx'
import { CharsCounter } from '../charsCounter'
import { useMention, useMentionUtils } from '../../hooks'
import { Wrapper } from './richTextInput.styles'
import { RichTextInputProps } from './richTextInput.types'
import { COMMENT_LIMIT } from './richTextInput.constants'

const RichTextInput = ({
  value,
  autoFocus,
  commentSuggestionText,
  placeholder,
  showCounter,
  readOnly,
  blur,
  enableMentions,
  className,
  onChange,
  onFocus,
  onKeyDown,
  onMentionSuccess,
  onMentionError,
  onMentionClick
}: RichTextInputProps) => {
  const [rawValue, setRawValue] = useState('')
  const mentionContainerRef = useRef<HTMLDivElement>(null)
  const { mentionsEditorExtensions } = useMention({
    enable: enableMentions,
    mentionConfig: {
      appendTo: /* istanbul ignore next */ () => mentionContainerRef.current,
      zIndex: 2
    },
    onMentionSuccess,
    onMentionError,
    onMentionClick
  })
  const { convertMentionToRawText, extractMentionsFromHTML, parseMentions } = useMentionUtils()

  const handleParseMentions = (editor: Editor) => {
    const value = editor.getText()
    const html = editor.getHTML()
    const mentions = extractMentionsFromHTML(html)
    const parsedComment = parseMentions({ text: value, mentions })
    const rawValue = convertMentionToRawText({
      text: value,
      mentions: mentions?.map((mention) => ({
        tag: mention.id,
        name: mention.label
      }))
    })

    setRawValue(rawValue)
    onChange?.({ value: parsedComment, mentions })
  }

  /* istanbul ignore next */
  const editor = useEditor({
    immediatelyRender: true,
    shouldRerenderOnTransaction: false,
    autofocus: autoFocus ? 'end' : false,
    editorProps: {
      handleDOMEvents: {
        keydown: (_: EditorView, event: KeyboardEvent) => {
          return event.key === 'Enter'
        }
      }
    },
    extensions: [
      Document,
      Text,
      Paragraph,
      CharacterCount.configure({
        limit: COMMENT_LIMIT
      }),
      ...mentionsEditorExtensions,
      Placeholder.configure({
        placeholder
      })
    ],
    content: value,
    onUpdate: ({ editor }) => {
      handleParseMentions(editor)
    },
    onFocus: () => {
      onFocus?.()
    }
  })

  const updateValue = (value: string) => {
    editor?.commands.setContent(value, false, {
      preserveWhitespace: 'full'
    })
  }

  useEffect(() => {
    if (!value.trim().length) {
      updateValue('')
    }
  }, [value])

  useEffect(() => {
    if (blur) {
      editor?.commands.blur()
    }
  }, [editor, blur])

  useEffect(() => {
    if (autoFocus) {
      handleParseMentions(editor)
    }
  }, [autoFocus])

  useEffect(() => {
    if (commentSuggestionText) {
      updateValue(commentSuggestionText)
      handleParseMentions(editor)
    }
  }, [commentSuggestionText])

  useEffect(() => {
    if (!showCounter) {
      setRawValue('')
    }
  }, [showCounter])

  return (
    <Wrapper ref={mentionContainerRef} className="_position-relative" readOnly={readOnly}>
      <EditorContent
        editor={editor}
        onKeyDown={onKeyDown}
        className={clsx(className, {
          'editor-autofocus': autoFocus,
          'editor-blur': blur
        })}
      />

      {showCounter && <CharsCounter value={rawValue} limit={COMMENT_LIMIT} />}
    </Wrapper>
  )
}

export default RichTextInput
